// Component: OrderPricingControls.jsx
// Purpose: 10509-15 R2 — the ONE shared order-pricing picker, used on both the Strategy Builder
//   (TradeExecutionStep, strategy-level — applies to every entry and exit) and the live-settings
//   modal (LiveDeploymentSettingsModal, per-account). Four fields only: entry reference (LTP /
//   Order book), entry side (AUTO/BID/ASK, shown ONLY when entry reference is ORDERBOOK), entry
//   offset, and exit reference (LTP / Order book). NO exit side or exit offset (R3 — every exit
//   inherits its reference at ONE chokepoint server-side; the exit lane is never side/offset-
//   authored here, mirrors the deleted per-node picker's own C3 constraint).
//
//   Fully controlled: `value` holds the 4 canonical keys (entry_reference_type,
//   entry_bid_ask_side, entry_price_offset, exit_reference_type) this component never owns state.
//   `defaults` (the GET envelope's `data.defaults`) is used ONLY to pre-fill a field the `value`
//   row does not yet carry — this component hard-codes NO fallback of its own ('LTP'/'AUTO'/
//   'ORDERBOOK' never appear as a JS default anywhere below); an un-authored field with no
//   `defaults` entry either renders blank or falls through to the platform default the backend's
//   own `normalize_order_pricing` already applied before this component ever saw the row.
//
//   10509-15 iteration 2 (QA H1) — `onChange` updates LOCAL state ONLY (every keystroke); it never
//   triggers a network write itself. `onCommit` (optional; defaults to `onChange` for backward
//   compat) fires once per discrete edit — a Segmented choice (immediate, one choice) or the
//   offset <input>'s blur/Enter — and is the ONLY signal callers should wire to a PUT. This mirrors
//   the modal's own explicit "Save changes" idiom: nothing here auto-saves on every keystroke.
//
//   10509-15 iteration 3 (visual polish, Deepak: "crude, not well thought, not modern") — rebuilt
//   on DS primitives instead of a flat grid of <Select>s: the reference choice (entry + exit) is a
//   `Segmented` ("Order book" / "LTP") with a one-line meaning underneath that updates with the
//   choice; entry side is a `Segmented` ("Auto"/"Bid"/"Ask"). Behaviour, props, commit semantics
//   and the data contract above are UNCHANGED — this is markup only.
//
//   10509-15 iteration 4 (visual polish #2, Deepak: "much better but not perfect, make it more
//   neat and compact") — cuts panel height roughly in half:
//     - the per-section helper PARAGRAPHS (QA3's "How the limit price is set…" / "How exits are
//       priced…") move into a Tooltip on the Entry/Exit section title's own (i) icon (same trigger
//       pattern iteration 3 used for Entry side's (i)) instead of always-on prose.
//     - every field label (Reference / Side / Offset) uses the SAME small-caps caption style
//       (`FieldLabel` below) — no more a bold ad-hoc "Entry offset" label floating at a different
//       size.
//     - Entry's reference + side + offset sit on ONE aligned row (flex, items-end, wraps to one
//       field per line on phone width); the offset input is compact (w-28) with a "pts" suffix
//       instead of stretching full width. Exit is ONE row: reference Segmented + a short inline
//       "Side and buffer are platform-set." note — no exit helper paragraph.
//     - the two per-section "Platform default" badges are replaced by ONE combined status chip —
//       `PlatformDefaultsChip` (exported) — meant to sit in the HOST's own header line next to
//       "Order pricing" (TradeExecutionStep) / the account id (LiveDeploymentSettingsModal), not
//       floating inside this component's own body; computed from the SAME `isFieldDefaulted`
//       authority this file already owned, so the two callers need not re-derive the logic.

import React from 'react';
import { Segmented, Input, Badge, Tooltip } from '../ds';

//! Canonical vocabulary (backend/shared/pricing/order_pricing.py REFERENCE_TYPES/BID_ASK_SIDES) —
//! mirrored here as the FE's own copy, same discipline as LiveDeploymentSettingsModal's
//! ORDER_TYPE_OPTIONS / PRODUCT_TYPE_OPTIONS (no shared FE/BE import exists for enums today).
const REFERENCE_TYPE_OPTIONS = [
  { value: 'ORDERBOOK', label: 'Order book' },
  { value: 'LTP', label: 'LTP' },
];
const BID_ASK_SIDE_OPTIONS = [
  { value: 'AUTO', label: 'Auto' },
  { value: 'BID', label: 'Bid' },
  { value: 'ASK', label: 'Ask' },
];

//--> One-line meaning for whichever reference is currently selected — shown under the Segmented
//    so the choice is self-explaining instead of a bare acronym. Shortened in iteration 4 to fit
//    one line at panel width.
const REFERENCE_MEANING = {
  ORDERBOOK: 'Best bid/ask on the side that fills now.',
  LTP: 'Last traded price — may sit unfilled.',
};

const ALL_FIELDS = ['entry_reference_type', 'entry_bid_ask_side', 'entry_price_offset', 'exit_reference_type'];

//--> QA M12/D37 — is `field` backend-flagged as NOT operator-authored? Whole-row
//    `pricing_source === 'platform_default'`, this field listed in `defaults_filled`, or there is
//    no row at all (`isEmptyRow`) so everything shown is sourced from `defaults`.
function isFieldDefaulted(field, row, isEmptyRow) {
  const filledList = Array.isArray(row.defaults_filled) ? row.defaults_filled : [];
  return row.pricing_source === 'platform_default' || filledList.includes(field) || isEmptyRow;
}

//--> Iteration 4 — ONE combined summary across all 4 fields (replaces the old per-section
//    `entryDefaulted`/`exitDefaulted` pair). Exported so both host wrappers can place the status
//    chip in THEIR OWN header line (next to "Order pricing" / the account id) without re-deriving
//    the defaulted-field rule.
export function getPlatformDefaultsSummary(value, defaults) {
  const row = value || {};
  const isEmptyRow = Object.keys(row).length === 0;
  const filledList = Array.isArray(row.defaults_filled) ? row.defaults_filled : [];
  const defaultedFields = ALL_FIELDS.filter((f) => isFieldDefaulted(f, row, isEmptyRow));
  return {
    anyDefaulted: defaultedFields.length > 0,
    fields: filledList.length > 0 ? filledList : defaultedFields,
  };
}

//--> Iteration 4 — the ONE quiet status chip for the panel header (was two outlined "Platform
//    default" pills, one per section). Soft tone (no heavy border), muted text; the Tooltip lists
//    exactly which fields are defaulted so the chip stays a single word at a glance.
export function PlatformDefaultsChip({ value, defaults, className }) {
  const { anyDefaulted, fields } = getPlatformDefaultsSummary(value, defaults);
  if (!anyDefaulted) return null;
  const title = fields.length > 0
    ? `Platform default (not yet authored): ${fields.join(', ')}`
    : 'Platform default (not yet authored)';
  return (
    <Badge size="xs" variant="neutral" tone="soft" title={title} className={className || 'shrink-0'}>
      Platform defaults
    </Badge>
  );
}

//--> Iteration 4 — the uniform small-caps field caption (Reference / Side / Offset), optionally
//    with an info Tooltip trigger — same visual language as a section title's own (i) icon.
function FieldLabel({ children, tooltip }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[10px] uppercase tracking-widest text-fg-3">{children}</span>
      {tooltip && (
        <Tooltip content={tooltip} placement="top">
          <span className="text-fg-3 text-[10px] leading-none cursor-help" aria-hidden="true">ⓘ</span>
        </Tooltip>
      )}
    </div>
  );
}

//--> Iteration 4 — section title (Entry / Exit) with its helper folded into an (i) Tooltip instead
//    of an always-on paragraph. Title style UNCHANGED from iteration 3.
function SectionTitle({ children, tooltip }) {
  return (
    <div className="flex items-center gap-1">
      <h5 className="text-[11px] font-black uppercase tracking-wider text-fg-2">{children}</h5>
      <Tooltip content={tooltip} placement="top">
        <span className="text-fg-3 text-[10px] leading-none cursor-help" aria-hidden="true">ⓘ</span>
      </Tooltip>
    </div>
  );
}

export default function OrderPricingControls({ value, defaults, onChange, onCommit, disabled }) {
  const row = value || {};
  const def = defaults || {};

  //--> Pick the row's own authored value; only fall through to the server-supplied `defaults`
  //    when the row genuinely has not authored this field yet (null/undefined) — never a
  //    hard-coded literal.
  const fieldValue = (field) => (row[field] ?? def[field] ?? '');

  //--> QA H1 — `onChange` is LOCAL-state-only on every keystroke; `onCommit` (falls back to
  //    `onChange` when the caller does not wire one, so existing callers keep working) is the
  //    ONE signal a caller should turn into a PUT. `emit` always fires `onChange`; `commitSelect`
  //    additionally fires `onCommit` with the merged row — used by the Segmented controls (one
  //    discrete choice, commits immediately) and by the offset <Input>'s onBlur/Enter (see below).
  //    `Segmented.onChange` already hands back the raw option value (not an event), same shape a
  //    native <select>'s `e.target.value` would unwrap to — no change needed to these helpers.
  const emit = (field) => (eOrVal) => {
    const raw = eOrVal && eOrVal.target ? eOrVal.target.value : eOrVal;
    onChange?.({ ...row, [field]: raw });
  };

  const commitSelect = (field) => (eOrVal) => {
    const raw = eOrVal && eOrVal.target ? eOrVal.target.value : eOrVal;
    const merged = { ...row, [field]: raw };
    onChange?.(merged);
    (onCommit || onChange)?.(merged);
  };

  //--> Offset input: `onChange` updates local state only on every keystroke (no PUT); commit
  //    fires on blur or Enter, with the LATEST local value (the event target's own value, not a
  //    stale `row` closure — the input is controlled so `row.entry_price_offset` already reflects
  //    the last onChange by the time blur/Enter fires, but we read the DOM value directly to be
  //    exact even if a caller batches state updates).
  const commitOffset = (e) => {
    const raw = e?.target ? e.target.value : e;
    (onCommit || onChange)?.({ ...row, entry_price_offset: raw });
  };
  const handleOffsetKeyDown = (e) => {
    if (e.key === 'Enter') commitOffset(e);
  };

  const entryReference = fieldValue('entry_reference_type');
  const showSide = entryReference === 'ORDERBOOK';
  const exitReference = fieldValue('exit_reference_type');

  return (
    <div className="space-y-3">
      {/*>>> Entry pricing <<<*/}
      <div className="space-y-2">
        <SectionTitle tooltip="How the limit price is set when a trade opens.">Entry</SectionTitle>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <FieldLabel>Reference</FieldLabel>
            <Segmented
              aria-label="Entry reference"
              value={entryReference}
              onChange={commitSelect('entry_reference_type')}
              options={REFERENCE_TYPE_OPTIONS}
              disabled={disabled}
            />
          </div>

          {showSide && (
            <div className="space-y-1">
              <FieldLabel tooltip="Auto = buy at ask, sell at bid.">Side</FieldLabel>
              <Segmented
                aria-label="Entry side"
                value={fieldValue('entry_bid_ask_side')}
                onChange={commitSelect('entry_bid_ask_side')}
                options={BID_ASK_SIDE_OPTIONS}
                disabled={disabled}
              />
            </div>
          )}

          <div className="space-y-1 w-28">
            <FieldLabel>Offset</FieldLabel>
            <Input
              size="sm"
              aria-label="Entry offset"
              type="number"
              step="0.05"
              value={fieldValue('entry_price_offset')}
              onChange={emit('entry_price_offset')}
              onBlur={commitOffset}
              onKeyDown={handleOffsetKeyDown}
              disabled={disabled}
              suffix="pts"
            />
          </div>
        </div>
        <p className="text-[10px] text-fg-3 leading-snug">
          {REFERENCE_MEANING[entryReference] || 'Choose how the entry price is referenced.'}
        </p>
      </div>

      <div className="border-t border-[var(--divider)]" />

      {/*>>> Exit pricing — reference ONLY, no side/offset (C3/R3) <<<*/}
      <div className="space-y-2">
        <SectionTitle tooltip="How exits are priced — signal exits, stop-loss conversion, chase and cutoff closes. The side and buffer are set by the platform (always the side that fills).">
          Exit
        </SectionTitle>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <FieldLabel>Reference</FieldLabel>
            <Segmented
              aria-label="Exit reference"
              value={exitReference}
              onChange={commitSelect('exit_reference_type')}
              options={REFERENCE_TYPE_OPTIONS}
              disabled={disabled}
            />
          </div>
          <span className="text-[10px] text-fg-3 leading-snug">Side and buffer are platform-set.</span>
        </div>
      </div>
    </div>
  );
}
